import { Component } from '@theme/component';
import { ThemeEvents, PurchaseOptionUpdateEvent } from '@theme/events';
import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

// Shopify's events module comes from its CDN, which content blockers sometimes stop. A static
// import would take this whole module down with it and leave the tiers locked, so load it lazily.
const standardEvents = import('@shopify/events').then(({ StandardEvents }) => StandardEvents);
standardEvents.catch(() => {});

/** First-order factors on top of the plan's 5%: 2 tins reach 10% off MSRP, 3+ reach 15%. */
const FIRST_ORDER = { 1: 1, 2: 0.9472, 3: 0.8947 };

/**
 * Quantity-aware estimates. Shopify remains the authority for checkout discounts.
 *
 * On single tins with a mix box (snippets/razzy-purchase-mix.liquid), the 1/2/3 tin buttons size
 * a box of tins (new tiles start as this tin) and each tile's picker changes, adds or removes
 * one. This tin's count goes
 * in the product form's quantity; the other tins are form-associated items[] fields, so the
 * theme's own Add to cart sends the whole box in one request.
 */
class RazzyPurchaseOptions extends Component {
  #listeners;
  #request;
  #cartRevision = 0;
  #tier = 1;
  /** The box's tins after this one (this tin itself when doubled up). @type {HTMLElement[]} */
  #extras = [];

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    document.addEventListener('input', this.#handleQuantity, { signal });
    document.addEventListener('change', this.#handleQuantity, { signal });
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#update, { signal });
    this.#listenForCart(signal);
    const input = this.#quantityInput();
    this.#tier = Math.min(3, Math.max(1, parseInt(input?.value || '1') || 1));
    const self = this.#self;
    this.#extras = self ? Array(this.#tier - 1).fill(self) : [];
    if (this.#mix) this.#applyMix();
    this.#update();
    this.#refreshCart();
  }

  /** @param {AbortSignal} signal */
  async #listenForCart(signal) {
    try {
      const StandardEvents = await standardEvents;
      if (!signal.aborted) document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCart, { signal });
    } catch (error) {
      console.warn('[purchase-options] Cart events unavailable; using the cart at page load', error);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    // A section re-render replaces the catalog; point the picks at its new nodes.
    const tins = this.#tins;
    this.#extras = this.#extras
      .map((pick) => tins.find((tin) => tin.dataset.variantId === pick.dataset.variantId))
      .filter((tin) => tin instanceof HTMLElement);
    this.#tier = this.#extras.length + 1;
    if (this.#mix) this.#applyMix();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#cartRevision++;
  }

  get #mix() {
    const mix = this.querySelector('[data-rz-mix]');
    return mix instanceof HTMLElement ? mix : null;
  }

  /** Every tin that can join the box, from the hidden catalog. @returns {HTMLElement[]} */
  get #tins() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-rz-tin]')]);
  }

  get #self() {
    const self = this.querySelector('[data-rz-tin][data-rz-self]');
    return self instanceof HTMLElement ? self : null;
  }

  #quantityInput() {
    const form = document.getElementById(this.dataset.formId);
    const input = form instanceof HTMLFormElement ? form.elements.namedItem('quantity') : null;
    return input instanceof HTMLInputElement ? input : null;
  }

  #subscribeRadio() {
    const radio = this.#mix?.closest('[data-rz-plan]')?.querySelector('[data-rz-subscribe]');
    return radio instanceof HTMLInputElement ? radio : null;
  }

  /** Sets the box (or, without one, the form quantity) from a tin preset and selects its subscription. @param {MouseEvent} event */
  selectQuantity(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-rz-tier]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const radio = button.closest('[data-rz-plan]')?.querySelector('[data-rz-subscribe]');
    if (radio instanceof HTMLInputElement) radio.checked = true;
    const tier = Number(button.dataset.rzTier);
    const self = this.#self;
    if (self && this.#mix && button.closest('[data-rz-plan]')?.contains(this.#mix)) {
      // Growing fills the new tiles with this tin; shrinking drops the last picks.
      const fresh = [];
      this.#extras = Array.from({ length: tier - 1 }, (_, index) => {
        if (this.#extras[index]) return this.#extras[index];
        fresh.push(index);
        return self;
      });
      this.#tier = tier;
      this.#applyMix(...fresh);
    } else {
      this.#setQuantity(tier);
    }
    this.#update();
  }

  /** A tile's picker: change its tin, add a tin on the "+" tile, or remove it. @param {Event} event */
  pickTin(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const index = [...this.querySelectorAll('select[data-rz-slot]')].indexOf(select);
    const value = select.value;
    if (index < 0 || !value) return;
    let fresh = [];
    if (value === 'remove') {
      this.#extras.splice(index, 1);
      this.#tier = this.#extras.length + 1;
    } else {
      const tin = this.#tins.find((candidate) => candidate.dataset.variantId === value);
      if (!tin) return;
      if (index < this.#extras.length) {
        this.#extras[index] = tin;
      } else if (this.#tier < 3) {
        this.#extras.push(tin);
        this.#tier = this.#extras.length + 1;
      }
      fresh = [index];
    }
    const radio = this.#subscribeRadio();
    if (radio) radio.checked = true;
    this.#applyMix(...fresh);
    this.#update();
  }

  /** Fills the box with a ready-made trio. @param {MouseEvent} event */
  loadPreset(event) {
    const preset = event.target;
    if (!(preset instanceof HTMLButtonElement)) return;
    const tins = this.#tins;
    const picks = (preset.dataset.variants || '').split(',').map((id) => tins.find((tin) => tin.dataset.variantId === id));
    if (picks.length !== 2 || !picks.every(Boolean)) return;
    this.#extras = /** @type {HTMLElement[]} */ (picks);
    this.#tier = 3;
    const radio = this.#subscribeRadio();
    if (radio) radio.checked = true;
    this.#applyMix(0, 1);
    this.#update();
  }

  /** @param {number} value */
  #setQuantity(value) {
    const input = this.#quantityInput();
    const selector = input?.closest('quantity-selector-component');
    if (!(selector instanceof QuantitySelectorComponent) || !input || input.disabled) return;
    if (input.value === String(value)) return;
    selector.setValue(String(value));
    selector.onQuantityChange();
    selector.updateButtonStates();
  }

  /** @param {number[]} fresh - Tile indexes that just filled, for the pop-in */
  #applyMix(...fresh) {
    const mix = this.#mix;
    const self = this.#self;
    if (!mix || !self) return;
    const box = [self, ...this.#extras];
    if (this.#subscribeRadio()?.checked) this.#setQuantity(box.filter((tin) => tin === self).length);

    mix.querySelectorAll('select[data-rz-slot]').forEach((select, index) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const item = select.closest('[data-rz-slot-item]');
      if (!(item instanceof HTMLElement)) return;
      const tin = this.#extras[index];
      // Tiles after the box's tins: the first is the "+" tile, any further one hides.
      item.hidden = !tin && index > this.#extras.length;
      item.classList.toggle('is-filled', Boolean(tin));
      item.classList.toggle('is-new', fresh.includes(index));
      const art = item.querySelector('.rz-mix__slot-art');
      const name = item.querySelector('.rz-mix__slot-name');
      const remove = select.querySelector('[data-rz-remove]');
      if (remove instanceof HTMLOptionElement) remove.disabled = remove.hidden = !tin;
      if (tin) {
        item.setAttribute('style', tin.getAttribute('style') || '');
        art?.replaceChildren(...[...tin.childNodes].map((node) => node.cloneNode(true)));
        if (name) name.textContent = tin.dataset.name || '';
        select.value = tin.dataset.variantId || '';
        select.setAttribute('aria-label', (this.dataset.labelSlot || '')
          .replace('__NUMBER__', select.dataset.number || '').replace('__PRODUCT__', tin.dataset.name || ''));
      } else {
        item.removeAttribute('style');
        art?.replaceChildren(Object.assign(document.createElement('span'), { className: 'rz-mix__plus', textContent: '+' }));
        if (name) name.textContent = this.dataset.addShort || '';
        select.selectedIndex = 0;
        select.setAttribute('aria-label', (this.dataset.labelAdd || '').replace('__PERCENT__', String((this.#tier + 1) * 5)));
      }
    });

    const picked = this.#extras.map((tin) => tin.dataset.variantId).sort().join(',');
    for (const preset of mix.querySelectorAll('[data-rz-preset]')) {
      const ids = (preset instanceof HTMLElement ? preset.dataset.variants || '' : '').split(',').sort().join(',');
      preset.setAttribute('aria-pressed', String(this.#tier === 3 && ids === picked));
    }

    this.#writeFields();

    const status = mix.querySelector('[data-rz-mix-status]');
    const message = (this.dataset.status || '').replace('__TINS__', box.map((tin) => tin.dataset.name).join(', '));
    if (status && status.textContent !== message) status.textContent = message;
  }

  /** The other tins in the box, as items[] fields on the product form while subscribing. */
  #writeFields() {
    const mix = this.#mix;
    const self = this.#self;
    const fields = mix?.querySelector('[data-rz-mix-fields]');
    if (!mix || !self || !fields) return;
    const radio = this.#subscribeRadio();
    const counts = new Map();
    if (radio?.checked) {
      for (const tin of this.#extras) {
        if (tin !== self) counts.set(tin.dataset.variantId, (counts.get(tin.dataset.variantId) || 0) + 1);
      }
    }
    const inputs = [];
    let index = 0;
    for (const [id, quantity] of counts) {
      for (const [key, value] of [['id', id], ['quantity', quantity], ['selling_plan', radio?.value]]) {
        const input = Object.assign(document.createElement('input'), { type: 'hidden', name: `items[${index}][${key}]`, value: String(value) });
        input.setAttribute('form', mix.dataset.formId || '');
        inputs.push(input);
      }
      index++;
    }
    fields.replaceChildren(...inputs);
    mix.toggleAttribute('data-mixed', inputs.length > 0);
  }

  /** @param {Event} event */
  #handleQuantity = (event) => {
    // The product form's input named id shadows form.id. Compare the form element itself.
    if (!(event.target instanceof HTMLInputElement) || event.target.form !== document.getElementById(this.dataset.formId)) return;
    if (event.type === 'change' && event.target.name === 'selling_plan' && this.#mix) {
      // One-time is a single tin of its own; the box stays with the subscription.
      if (event.target.hasAttribute('data-rz-one-time')) this.#setQuantity(1);
      this.#applyMix();
    }
    this.#update();
  };

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #handleCart = async (event) => {
    const revision = ++this.#cartRevision;
    this.#request?.abort();
    try {
      await event.promise;
      if (!this.isConnected || revision !== this.#cartRevision) return;
      await this.#refreshCart();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[purchase-options] Cart update unavailable', error);
    }
  };

  async #refreshCart() {
    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;
    try {
      const response = await fetch(this.dataset.cartUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await response.json();
      if (controller.signal.aborted || !this.isConnected) return;
      for (const plan of this.querySelectorAll('[data-rz-plan]')) {
        plan.dataset.cartQuantity = String(cart.items.reduce((count, item) =>
          String(item.selling_plan_allocation?.selling_plan?.id) === plan.dataset.planId ? count + item.quantity : count, 0));
      }
      this.#update();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[purchase-options] Using last known cart quantity', error);
    }
  }

  #update = () => {
    const form = document.getElementById(this.dataset.formId);
    if (!(form instanceof HTMLFormElement)) return;
    const quantityInput = form.elements.namedItem('quantity');
    const quantity = quantityInput instanceof HTMLInputElement ? Math.max(1, parseInt(quantityInput.value) || 1) : 1;
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency,
    });
    const format = (cents) => money.format(cents / 100);
    const once = this.querySelector('[data-rz-once-price]');
    let selection = { quantity, price: '', subscription: false };
    const subscribed = Boolean(this.querySelector('[data-rz-subscribe]:checked'));
    // While a box is open the form quantity is only this tin's share of it; one-time shows one tin.
    const onceQuantity = subscribed && this.#mix && this.#self ? 1 : quantity;
    if (once) {
      const amount = once.querySelector('.rz-price__current bdi');
      const total = format(Number(once.dataset.unitPrice) * onceQuantity);
      selection.price = total;
      if (amount) amount.textContent = total;
      for (const price of this.closest('.shopify-section')?.querySelectorAll('[data-rz-one-time-price]') || []) {
        const value = price.querySelector('.rz-price__current bdi, .price > bdi');
        if (value) value.textContent = total;
      }
    }
    const self = this.#self;
    for (const plan of this.querySelectorAll('[data-rz-plan]')) {
      // Each tin's MSRP and renewal price: the box when this plan holds it, else this tin × quantity.
      const own = { price: Number(plan.dataset.unitPrice), renewal: Number(plan.dataset.renewalUnit) };
      const boxed = Boolean(self && this.#mix && plan.contains(this.#mix));
      const lines = boxed
        ? [self, ...this.#extras].map((tin) => ({ price: Number(tin.dataset.price), renewal: Number(tin.dataset.renewal) }))
        : Array(quantity).fill(own);
      const count = lines.length;
      const tier = Math.min(3, count + Number(plan.dataset.cartQuantity || 0));
      const percent = tier * 5;
      const sum = (read) => lines.reduce((total, line) => total + read(line), 0);
      const msrp = sum((line) => line.price);
      const renewal = sum((line) => line.renewal);
      const first = sum((line) => Math.round(line.renewal * FIRST_ORDER[tier]));
      const update = (selector, value) => {
        const node = plan.querySelector(selector);
        if (node && node.textContent !== value) node.textContent = value;
      };
      const interpolate = (selector, token, value) => {
        const node = plan.querySelector(selector);
        if (node) update(selector, node.dataset.pattern.replace(token, String(value)));
      };
      interpolate('[data-rz-savings]', '__PERCENT__', percent);
      interpolate('[data-rz-renewal]', '__PRICE__', format(renewal));
      const cartNote = plan.querySelector('[data-rz-cart-note]');
      if (cartNote) cartNote.hidden = Number(plan.dataset.cartQuantity || 0) === 0;
      update('[data-rz-first-price]', format(first));
      update('[data-rz-msrp]', format(msrp));
      for (const step of plan.querySelectorAll('[data-rz-tier]')) {
        const preset = Number(step.dataset.rzTier);
        step.setAttribute('aria-pressed', String(Math.min(3, count) === preset));
        if (step instanceof HTMLButtonElement && quantityInput instanceof HTMLInputElement && !boxed) {
          const selector = quantityInput.closest('quantity-selector-component');
          const min = Number(quantityInput.min) || 1;
          const increment = Number(quantityInput.step) || 1;
          const max = selector instanceof QuantitySelectorComponent ? selector.getEffectiveMax() : null;
          step.disabled = quantityInput.disabled || preset < min || (max !== null && preset > max) || (preset - min) % increment !== 0;
        }
      }
      if (plan.querySelector('[data-rz-subscribe]:checked')) {
        selection = { quantity: count, price: format(first), subscription: true };
        const offers = this.closest('.shopify-section')?.querySelectorAll('[data-rz-single-offer]') || [];
        for (const offer of offers) {
          const set = (selector, value) => {
            const node = offer.querySelector(selector);
            if (node && node.textContent !== value) node.textContent = value;
          };
          set('[data-rz-single-saving]', plan.querySelector('[data-rz-savings]').textContent);
          set('.rz-price__current bdi', format(first));
          set('.rz-price__msrp bdi', format(msrp));
          set('.rz-price__saving', this.dataset.savingPattern.replace('__PERCENT__', String(percent)));
        }
      }
    }
    this.dispatchEvent(new PurchaseOptionUpdateEvent(selection));
  };
}

if (!customElements.get('razzy-purchase-options')) {
  customElements.define('razzy-purchase-options', RazzyPurchaseOptions);
}
